import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../App';
import { toast } from 'sonner';

// --- ASSETS ---
export const useAssets = () => {
  return useQuery({
    queryKey: ['assets'],
    queryFn: async () => {
      const response = await api.get('/assets');
      return response.data;
    },
    refetchInterval: 10000, // Poll every 10s
    refetchIntervalInBackground: true
  });
};

export const useAsset = (assetId) => {
  return useQuery({
    queryKey: ['asset', assetId],
    queryFn: async () => {
      const response = await api.get(`/assets/${assetId}`);
      return response.data;
    },
    enabled: !!assetId,
    refetchInterval: 5000, // Poll every 5s (active viewing)
    refetchIntervalInBackground: true
  });
};

// --- COMMITMENTS ---
export const useCommitments = (status = 'all') => {
  return useQuery({
    queryKey: ['commitments', status],
    queryFn: async () => {
      // Handle 'all' or specific status
      const endpoint = status === 'all' ? '/commitments' : `/commitments?status=${status}`;
      const response = await api.get(endpoint);
      return response.data;
    },
    refetchInterval: 5000, // Poll every 5s (high priority)
    refetchIntervalInBackground: true
  });
};

// --- PAYOUTS ---
export const usePayouts = () => {
  return useQuery({
    queryKey: ['payouts'],
    queryFn: async () => {
      const response = await api.get('/payouts');
      return response.data;
    },
    refetchInterval: 10000, // Poll every 10s
    refetchIntervalInBackground: true
  });
};

// --- ADMIN STATS ---
export const useAdminStats = () => {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data;
    },
    refetchInterval: 5000, // Poll every 5s (snappy dashboard)
    refetchIntervalInBackground: true
  });
};

// --- NOTIFICATIONS ---
export const useNotifications = () => {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      return response.data;
    },
    refetchInterval: 5000, // Real-time notification updates
    refetchIntervalInBackground: true
  });
};

export const useNotificationCount = () => {
  return useQuery({
    queryKey: ['notifications-count'],
    queryFn: async () => {
      const response = await api.get('/notifications/count');
      return response.data.unread_count; // Assuming API returns { unread_count: X }
    },
    refetchInterval: 5000, // Real-time badge updates
    refetchIntervalInBackground: true
  });
};

// --- MUTATIONS (Examples) ---
export const useApproveCommitment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/commitments/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries(['commitments']);
      queryClient.invalidateQueries(['admin-stats']);
      toast.success('Commitment approved');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Approval failed')
  });
};

export const useRejectCommitment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => api.post(`/commitments/${id}/reject`, null, { params: { reason } }),
    onSuccess: () => {
      queryClient.invalidateQueries(['commitments']);
      queryClient.invalidateQueries(['admin-stats']);
      toast.success('Commitment rejected');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Rejection failed')
  });
};
